import {ListTableDataSourceFragment} from "@/shared/ListTableDataSourceFragment";
import {
  LABELER_TASK_CASE_STATUS_KTM,
  LabelerSegmentationType,
  LabelerTask,
  LabelerTaskCase,
  LabelerTaskCaseStatus
} from "@/fyneai-shared/types";
import {TableDataContainer, TableDataRow} from "@/shared/TableDataContainer";
import React from "react";
import {TableData} from "@/shared/tabledata";
import {PageFragment, PageFragmentProps, PageFragmentStyleFlags} from "@/shared/PageFragment";
import {Action, ACTION_SPACE, ActionBase, CreateActionsArgs, FixedListItemsLoader} from "@/shared/types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE, DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN} from "@/shared/BaseApp";
import {WorkbenchFragment} from "../../workbench/WorkbenchFragment";
import {TaskDoneHelper} from "./TaskDoneHelper";
import {DownloadOutlined} from "@mui/icons-material";
import {FileUtil} from "../../../../shared/file_util";

class LabelerTaskCaseRow implements TableDataRow {

  @TableData({name: "ID"})
  id: string;

  @TableData({name: "File", cellSize: "xxlarge"})
  fileUrl: string;

  @TableData({name: "Labels"})
  labels: string;

  @TableData({
    name: "Status",
    type: "enum",
    enumValues: LABELER_TASK_CASE_STATUS_KTM.kts().values,
    enumColors: LABELER_TASK_CASE_STATUS_KTM.metadataValues("color").values,
    cellSize: "large"
  })
  status: string;

  @TableData({
    name: "Actions", type: "actions", actions: (event, args: CreateActionsArgs<LabelerTaskCase>) => {
      return [
        new Action("Edit labels", () => {
          const id = BaseApp.CONTEXT.showDialog(
            {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN},
            () => <div style={{display: "flex", flexDirection: "column", width: "100dvw", height: "100dvh"}}>
              <WorkbenchFragment
                showNavigation={true}
                task={args.data.getTask()}
                initialSelection={args.data}
                onFinish={() => {
                  BaseApp.CONTEXT.hideDialog(id);
                  setTimeout(() => TaskDoneHelper.showDoneDialog(), 100);
                }}/>
            </div>,
          );
        })
      ]
    }
  })
  actions: any;
}

export type TaskCasesFragmentProps = PageFragmentProps & {
  task: LabelerTask,
}

class LabelerTaskCases extends FixedListItemsLoader<LabelerTaskCase> {
}

type LabelerTaskDownloadItemLabel = {
  class: string,
  shape?: LabelerSegmentationType,
  points?: number[],
}

type LabelerTaskDownloadItem = {
  url: string,
  labels: LabelerTaskDownloadItemLabel[],
}

export class TaskCasesFragment extends ListTableDataSourceFragment<LabelerTaskCase, LabelerTaskCaseRow, LabelerTaskCases, TaskCasesFragmentProps> {

  private readonly actions: ActionBase[] = [
    ACTION_SPACE,
    new Action("Download JSON", () => this.onDownloadJson(), DownloadOutlined).setVariant("contained"),
  ];

  constructor(props: TaskCasesFragmentProps, context: any) {
    super(props, context, new LabelerTaskCases(props.task.cases));
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG;
  }

  protected getTitle(): string | null | undefined {
    return "Cases";
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx(this.actions);
  }

  private onDownloadJson(): void {
    const task = this.props.task;
    const cases = task.cases;
    BaseApp.CONTEXT.showProgressDialog("Preparing data...", null, async function () {
      const items: LabelerTaskDownloadItem[] = [];
      for (const case_ of cases) {
        if (case_.status !== LabelerTaskCaseStatus.ACCEPTED || !(case_.labels?.length > 0)) {
          continue;
        }
        let item: LabelerTaskDownloadItem;
        for (const label of case_.labels) {
          if (label.answerClasses?.length > 0) {
            for (const answerClass of label.answerClasses) {
              if (!item) {
                item = {
                  url: case_.asset.fileUrl,
                  labels: [],
                }
                items.push(item);
              }
              item.labels.push({
                class: answerClass,
                shape: label.segmentation?.type,
                points: label.segmentation?.points,
              });
            }
          }
        }
      }
      const jsonString = JSON.stringify(items, null, 2);
      FileUtil.downloadBlob(new Blob([jsonString], {type: "application/json"}), "data.json");
    });
  }

  applyTableDataToRow(data: LabelerTaskCase, row: LabelerTaskCaseRow): void {
    row.id = data.asset.id;
    row.fileUrl = data.asset.fileUrl;
    row.labels = (data.labels?.length || 0).toString();
    row.status = data.status;
  }

  createTableDataRow(): LabelerTaskCaseRow {
    return new LabelerTaskCaseRow();
  }

  onTableCellChanged(data: LabelerTaskCase, cellId: string, value: any): void {
  }

  onTableDataSelected(data: LabelerTaskCase): void {
  }

  renderContent(): React.ReactElement {
    const items = this.loader.getListItems();
    return <TableDataContainer
      disableSelection={true}
      path={this.props.path}
      dataTotal={items.length}
      pageSize={10}
      data={(from: number, to: number) => this.processListItemsInRange(from, to, item => item.loadReturn())}
      source={this}
    />;
  }
}
